/**
 * ConversationDetailsModal
 * Modal com abas para detalhes de uma conversa: Info, Mídias, Membros (grupos), Privacidade
 */

import React from 'react';
import { Modal, ModalHeader, ModalContent } from '@/shared/components/ui/Modal';
import { Tabs } from '@/shared/components/ui/Tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { useConversation } from '../hooks';
import { useConversationMediaSummary } from '../hooks/useConversationMedia';
import { ConversationInfoTab } from './conversation-details/ConversationInfoTab';
import { ConversationMediaTab } from './conversation-details/ConversationMediaTab';
import { ConversationMembersTab } from './conversation-details/ConversationMembersTab';
import { ConversationPrivacyTab } from './conversation-details/ConversationPrivacyTab';
import type { TabItem } from '@/shared/components/ui/Tabs';

interface ConversationDetailsModalProps {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Quando definido, a modal mostra apenas essa aba (ex: 'privacy' ou 'members'). */
  onlyTab?: 'info' | 'media' | 'members' | 'privacy';
}

export const ConversationDetailsModal: React.FC<ConversationDetailsModalProps> = ({
  conversationId,
  isOpen,
  onClose,
  onlyTab,
}) => {
  const { data: conversation, isLoading } = useConversation(isOpen ? conversationId : undefined);
  // Badge da aba "Mídias" — count via endpoint paginado (antes vinha de conversation.files).
  const { data: mediaSummary } = useConversationMediaSummary(isOpen ? conversationId : undefined);

  const isGroup = conversation?.conversationType === 'group';
  const displayName =
    conversation?.group?.name ||
    conversation?.contact?.name ||
    conversation?.subject ||
    `Conversa #${conversationId.slice(0, 8)}`;
  const avatarUrl = conversation?.group?.picture || conversation?.contact?.picture;
  const phone = conversation?.contact?.phone;
  const channelName = conversation?.provider?.name || '';

  const fileCount = mediaSummary?.counts.total ?? 0;
  const memberCount = conversation?.members?.length ?? 0;

  const tabs: TabItem[] = [
    {
      id: 'info',
      label: 'Informações',
      icon: 'info',
      content: conversation ? <ConversationInfoTab conversation={conversation} /> : null,
    },
    {
      id: 'media',
      label: 'Mídias',
      icon: 'image',
      badge: fileCount,
      content: conversation ? <ConversationMediaTab conversation={conversation} /> : null,
    },
    ...(isGroup
      ? [
          {
            id: 'members',
            label: 'Membros',
            icon: 'users' as const,
            badge: memberCount,
            content: conversation ? <ConversationMembersTab conversation={conversation} /> : null,
          },
        ]
      : []),
    {
      id: 'privacy',
      label: 'Privacidade',
      icon: 'lock',
      content: conversation ? (
        <ConversationPrivacyTab conversation={conversation} onClose={onClose} />
      ) : null,
    },
  ];

  // Modo aba-única: filtra para a aba pedida (fallback para todas se não existir, ex: 'members' fora de grupo)
  const filtered = onlyTab ? tabs.filter((t) => t.id === onlyTab) : tabs;
  const visibleTabs = filtered.length > 0 ? filtered : tabs;
  const defaultTab = onlyTab && filtered.length > 0 ? onlyTab : 'info';

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl">
      <ModalHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10" isSquircle>
            <AvatarImage src={avatarUrl} alt={displayName} isSquircle />
            <AvatarFallback isSquircle>
              <span className="font-semibold" style={{ color: '#8b5cf6' }}>
                {displayName.charAt(0) || 'C'}
              </span>
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-base truncate" style={{ color: '#1e293b' }}>
              {displayName}
            </h2>
            <div className="flex items-center gap-2">
              {phone && (
                <span className="text-sm" style={{ color: '#6b7280' }}>
                  {phone}
                </span>
              )}
              {channelName && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}
                >
                  {channelName}
                </span>
              )}
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalContent padding={false} className="overflow-hidden">
        <div className="flex flex-col" style={{ minHeight: '420px', maxHeight: '70vh' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div
                className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: '#8b5cf6' }}
              />
            </div>
          ) : (
            <Tabs tabs={visibleTabs} defaultTab={defaultTab} variant="underline" />
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ConversationDetailsModal;
